import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import {
    Dialog,
    Grid,
    Button,
} from '@mui/material';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../ui/appDialogTitle';
import SafeDialogContent from '../../../ui/SafeDialogContent';
import ValidAccessoryBasicInfo from './validAccessoryBasicInfoForm';
import ValidFrameCostAndPricingForm from '../frames/validFrameCostAndPricingForm';
import { ValidDataService } from '../../../../service/validDataService';
import SupplierListDialog from '../../ValidData/validSupplier/supplierListDialog';
import ValidSupplierInputForm from '../../ValidData/validSupplier/validSupplierInputForm';
import validLookup from '../../../../utils/validLookup';
import { AttachmentService } from '../../../../service/attachmentService';
import accessoryValidationSchema, { initialValuesAccessoryForm } from './accessoryValidationSchema';
import useDialogModal from '../../../../hooks/useDialogModal';

export default function ValidAccessoryEditForm({ open, onClose, onRecordUpdated, recordToEdit }) {
    const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
    const [ValidSupplierInputDialog, showValidSupplierInputForm] = useDialogModal(ValidSupplierInputForm);

    const formik = useFormik({
        initialValues: {
            ...initialValuesAccessoryForm,
            ...recordToEdit,
            sku: recordToEdit.sku || '',
            supplierName: recordToEdit.supplier ? recordToEdit.supplier.supplierName : '',
            // brand: recordToEdit.brand.name,
            supplierCost: recordToEdit.supplierCost ? recordToEdit.supplierCost : '',
            landingCost: recordToEdit.landingCost ? recordToEdit.landingCost : '',
            listPrice: recordToEdit.listPrice ? recordToEdit.listPrice : '',
            profit: recordToEdit.profit ? recordToEdit.profit : '',
            profitPercentage: recordToEdit.profitPercentage ? recordToEdit.profitPercentage : '',
            lastOrdered: recordToEdit.lastOrdered ? recordToEdit.lastOrdered : '',
            lastReceived: recordToEdit.lastReceived ? recordToEdit.lastReceived : '',
            lastSold: recordToEdit.lastSold ? recordToEdit.lastSold : '',
            totalSold: recordToEdit.totalSold ? recordToEdit.totalSold : '',
            returned: recordToEdit.returned ? recordToEdit.returned : '',
            quantityOnOrder: recordToEdit.quantityOnOrder ? recordToEdit.quantityOnOrder : '',
            quantityOnHand: recordToEdit.quantityOnHand ? recordToEdit.quantityOnHand : '',
        },
        validationSchema: accessoryValidationSchema,
        onSubmit: (values) => {
            onSubmit(values);
        },
    });
    const { setFieldValue } = formik;

    useEffect(() => {
        (async () => {
            const attachments = await AttachmentService.getAttachment('accessories', recordToEdit.id)
            const { code } = attachments
            if (code !== 0) {
                toast.error('Failed to load attachments');
            }
        })()
    }, [recordToEdit])

    const onSubmit = async (values) => {
        formik.setSubmitting(true);
        try {
            const formattedValues = {
                ...values,
                supplierCost: values.supplierCost === '' ? null : parseFloat(values.supplierCost),
                landingCost: values.landingCost === '' ? null : parseFloat(values.landingCost),
                listPrice: values.listPrice === '' ? null : parseFloat(values.listPrice),
                photos: values.photos === '' ? null : values.photos,
                type: 'accessories',
                locationId: values.location.id,
                profit: values.profit === '' ? null : parseFloat(values.profit),
                profitPercentage: values.profitPercentage === '' ? null : parseFloat(values.profitPercentage),
                lastOrdered: values.lastOrdered === '' ? null : values.lastOrdered,
                lastReceived: values.lastReceived === '' ? null : values.lastReceived,
                lastSold: values.lastSold === '' ? null : values.lastSold,
                totalSold: values.totalSold === '' ? null : parseInt(values.totalSold, 10),
                returned: values.returned === '' ? null : parseInt(values.returned, 10),
                quantityOnOrder: values.quantityOnOrder === '' ? null : parseInt(values.quantityOnOrder, 10),
                quantityOnHand: values.quantityOnHand === '' ? null : parseInt(values.quantityOnHand, 10),
            };

            // Call the API to add the inventory
            const response = await ValidDataService.updateInventory(formattedValues);
            const { code, data, message } = response;

            formik.setSubmitting(false);

            if (code === 0) {
                toast.success(message);
                onRecordUpdated(data);
            } else {
                toast.error(message);
            }
        } catch (error) {
            console.error(error);
            formik.setSubmitting(false);
            toast.error('Failed to add record');
        }
    };

    const handleSupplierSelect = (supplier) => {
        setFieldValue('supplierId', supplier.id);
        setFieldValue('supplierName', supplier.supplierName);
        setFieldValue('brand', '');
        setFieldValue('brandId', null);
    };

    const handleSupplierCreated = (supplier) => {
        handleSupplierSelect(supplier);
    };

    return (
        <Dialog open={open} maxWidth={'lg'} fullWidth >
            <AppDialogTitle title="Edit Accessory" onClose={onClose} />
            <SafeDialogContent>
                <form onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2}>
                        <ValidAccessoryBasicInfo
                            formik={formik}
                            setSupplierDialogOpen={setSupplierDialogOpen}
                            setSupplierCreateDialogOpen={showValidSupplierInputForm}
                        />
                        <Grid item xs={12} sm={6}>
                            <ValidFrameCostAndPricingForm formik={formik} />
                        </Grid>
                        
                        <Grid item xs={12}>
                            <Button
                                type="submit"
                                variant="contained"
                                color="primary"
                                disabled={formik.isSubmitting}
                            >
                                Update
                            </Button>
                        </Grid>
                    </Grid>
                </form>
                <SupplierListDialog
                    open={supplierDialogOpen}
                    onClose={() => setSupplierDialogOpen(false)}
                    onSelect={handleSupplierSelect}
                    supplierType={validLookup.supplierType.accessories}
                />
                <ValidSupplierInputDialog
                    onRecordAdded={handleSupplierCreated}
                    initialValuesOverride={{ suppliesAccessories: true, status: 'active' }}
                />
            </SafeDialogContent>
        </Dialog>
    );
}